import { useMutation } from "@apollo/client";
import { RotateCw, ThumbsDown, ThumbsUp, UserRound } from "lucide-react";
import { useEffect, useState } from "react";
import ReactMarkdown from 'react-markdown';
import { AI_MESSAGE_LIKE_DISLIKE } from "../../apollo-client/graphql/chat-conversation/chat-conversation.mutation.js";
import { cn } from "../../lib/utils.js";
import { formatTimestampForMessage } from "../../utils/format-date.js";
import { useToast } from "../ui/use-toast.js";

const MessageItem = ({ message, isFirstInNewSlot, chatConfigurationStyle, messages, newSlotFirstMessageRef, keyIndex, handleSendMessage }) => {
  const { toast } = useToast()

  const [isLiked, setIsLiked] = useState(null);

  useEffect(() => {
    if (message) {
      setIsLiked(message?.isLiked)
    }
  }, [message])

  const [aiMessageLikeDisLikeMutation] = useMutation(AI_MESSAGE_LIKE_DISLIKE);

  const handleAiMessageLikeDisLike = async (newIsLiked, messageId) => {
    try {
      await aiMessageLikeDisLikeMutation({ variables: { input: { isLiked: newIsLiked, messageId, from: "webchat" } } });
      setIsLiked(newIsLiked);
    } catch (err) {
      toast({ description: err?.message || "Error while like or dislike AI message", variant: "destructive" });
    }
  };

  if (["aibot", "operator", "page", "system"].includes(message?.senderId)) {
    return (
      <div className={cn("flex items-start my-[20px] w-full")} key={keyIndex} ref={isFirstInNewSlot ? newSlotFirstMessageRef : undefined}>
          {(message?.senderId === "aibot" || message?.senderId === "system") ?
            <div className='bg-muted w-[30px] h-[30px] rounded-full mr-2'>
              <img className='object-contain w-full h-full' src={`${process.env.REACT_APP_S3_BASE_URL}/${chatConfigurationStyle?.profile}`} alt="Bot Avatar" />
            </div> :
            <div className="flex justify-center items-center w-[30px] h-[30px] rounded-full bg-muted p-2 mr-2">
              <UserRound className="w-full h-full" />
            </div>
          }
        <div className="max-w-[80%] message-container relative">
        <span className="text-[14px] font-medium my-1.5 block">{(message?.senderId === "aibot" || message?.senderId === "system") ? chatConfigurationStyle?.displayName: message?.userId?.name}</span>
          <span
            className="p-4 rounded-md ltr:rounded-tl-none rtl:rounded-tr-none text-[14px] block w-fit bg-accent"
          >
            <ReactMarkdown
              className="prose"
              components={{
                ol: ({ node, ...props }) => <ol style={{ listStyleType: 'decimal', paddingLeft: '1.5em' }} {...props} />,
                ul: ({ node, ...props }) => <ul style={{ listStyleType: 'disc', paddingLeft: '1.5em' }} {...props} />,
                // eslint-disable-next-line
                a: ({ node, ...props }) => <a style={{ textDecoration: 'underline' }} target="_blank" {...props} />,
                pre: ({ node, ...props }) => <pre style={{ backgroundColor: '#f0f0f0', padding: '1em', borderRadius: '4px', overflowX: 'auto' }} {...props} />,
              }}
            >{message?.message}</ReactMarkdown>
          </span>
          <div className="flex items-center mr-auto timestamp-container absolute bottom-0 left-0">
            <span className="text-muted-foreground text-xs mr-2">
              {formatTimestampForMessage(message?.timestamp)}
            </span>
            <div className="flex justify-between items-center">
              {(message?.senderId === "aibot" || message?.senderId === "system") && (
                <>
                  <ThumbsUp
                    fill={isLiked ? "currentColor" : "none"}
                    onClick={() => handleAiMessageLikeDisLike(true, message?._id)}
                    className="cursor-pointer w-3.5 h-3.5"
                  />
                  <ThumbsDown
                    fill={isLiked === false ? "currentColor" : "none"}
                    onClick={() => handleAiMessageLikeDisLike(false, message?._id)}
                    className="cursor-pointer w-3.5 h-3.5 ml-2"
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div
        className="flex gap-1 items-start my-[20px] w-full justify-end"
        key={keyIndex}
        ref={isFirstInNewSlot ? newSlotFirstMessageRef : undefined}
      >
        <div className="max-w-[80%] flex flex-col items-end message-container relative">
          <span
            className="p-4 rounded-md ltr:rounded-tr-none rtl:rounded-tl-none text-[14px] block w-fit bg-primary text-primary-foreground"
          >
            <ReactMarkdown
              className="prose"
              components={{
                ol: ({ node, ...props }) => <ol style={{ listStyleType: 'decimal', paddingLeft: '1.5em' }} {...props} />,
                ul: ({ node, ...props }) => <ul style={{ listStyleType: 'disc', paddingLeft: '1.5em' }} {...props} />,
                // eslint-disable-next-line
                a: ({ node, ...props }) => <a style={{ textDecoration: 'underline' }} target="_blank" {...props} />,
                pre: ({ node, ...props }) => <pre style={{ backgroundColor: '#f0f0f0', padding: '1em', borderRadius: '4px', overflowX: 'auto' }} {...props} />,
              }}
            >{message?.message}</ReactMarkdown>
          </span>
          <span className="flex justify-end text-muted-foreground text-xs timestamp-container absolute bottom-0 right-0">
            {(message?.isFailed) &&
              <span className="text-red-500 justify-around text-xs flex">
                Failed
                <RotateCw className="mt-[2px] w-[50%] h-[50%] cursor-pointer"
                  onClick={() => {
                    handleSendMessage({
                      message: message?.message
                    });
                  }} />
              </span>
            }
            {formatTimestampForMessage(message?.timestamp)}
          </span>
        </div>
        {/* <div className="flex justify-center items-center 500:w-9 w-8 500:h-9 h-8 rounded-full bg-muted p-2">
          <UserRound className="w-full h-full" />
        </div> */}
      </div>
    );
  }
};

export default MessageItem;